import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { TreeNode } from 'primeng/api'
import { PIMSProduct, VetCorProduct } from 'src/app/shared/services/ppv.api'
import { ProductFunctions } from '../functions'
import { OverlayPanel } from 'primeng/overlaypanel'
import { Tree, TreeNodeSelectEvent } from 'primeng/tree'
import { BehaviorSubject, Subject, combineLatest, map } from 'rxjs'

@Component({
   selector: 'vc-product-tree-select',
   templateUrl: './product-tree-select.component.html',
   styleUrls: ['./product-tree-select.component.scss'],
   standalone: false,
})
export class ProductTreeSelectComponent {
   public loading: boolean = true
   @Input() pimsProduct: PIMSProduct
   @Input() initialValue: number
   @Input() useCustomFilter: boolean
   @Input()
   set allProducts(value: TreeNode[]) {
      if (value !== undefined) {
         this._allProducts = value
         this.selectedProduct = ProductFunctions.findAndExpandCollapse({ children: this._allProducts }, this.initialValue, true)
         this._currentNodes$.next(value)
         this.loading = false
         if (this.selectedProduct && this.tree) {
            this.scroll()
         }
      }
   }
   @Output() onChanged = new EventEmitter<{
      pimsProduct: PIMSProduct
      newValue: number
   }>()

   @ViewChild('op') private overlayPanel: OverlayPanel
   @ViewChild('tree') private tree: Tree

   _allProducts: TreeNode[] = []
   selectedProduct: TreeNode<VetCorProduct>
   filterTextChanged$ = new BehaviorSubject<string>(undefined)

   private removeParents(node: TreeNode) {
      node.parent = undefined
      node.children?.forEach((child) => {
         this.removeParents(child)
      })
   }

   private _currentNodes$ = new BehaviorSubject<TreeNode[]>(this._allProducts)
   nodesToShow$ = combineLatest([this._currentNodes$, this.filterTextChanged$]).pipe(
      map(([nodes, filterText]) => {
         if (!filterText || !this.useCustomFilter) return nodes
         const newNodes: TreeNode[] = nodes.map((n) => {
            this.removeParents(n)
            return JSON.parse(JSON.stringify(n))
         })
         const splitFilterText = filterText.toLowerCase().split(' ')
         const validLeafNodes: TreeNode[] = []
         const allLeafNodes: TreeNode[] = []
         const allNodes: { [key: string]: TreeNode } = {}

         // Get all leaf nodes
         const recurseNodes = (node: TreeNode) => {
            if (node.selectable) {
               allLeafNodes.push(node)

               const splitLeafText = node.label?.toLowerCase().split(' ')

               let isValid = true
               for (let i = 0; isValid && i < splitFilterText.length; i++) {
                  isValid = isValid && !!splitLeafText.find((val) => val.indexOf(splitFilterText[i]) >= 0)
               }

               if (isValid) validLeafNodes.push(node)
            }

            allNodes[node.key] = node
            node.children?.forEach((c) => recurseNodes(c))
         }

         newNodes.forEach((n) => recurseNodes(n))

         // Build a hierarchy of valid paths
         const validHierarchicalNodes: { [key: string]: TreeNode } = {}

         const addValidHierarchicalNodes = (node: TreeNode) => {
            if (validHierarchicalNodes[node.key]) return

            if (node.children) node.expanded = true

            validHierarchicalNodes[node.key] = node

            if (node.data?.parentVCProductCategorySKey) addValidHierarchicalNodes(allNodes['c' + node.data?.parentVCProductCategorySKey])
            else if (node.data?.vcProductSKey) addValidHierarchicalNodes(allNodes['c' + node.data?.vcProductCategorySKey])
         }

         validLeafNodes.forEach((n) => {
            addValidHierarchicalNodes(n)
         })

         const filterHierarchicalNodes = (node: TreeNode) => {
            node.children = node.children?.filter((c) => !!validHierarchicalNodes[c.key])
            node.children?.forEach((c) => filterHierarchicalNodes(c))
         }

         // Remove children from each node where it doesn't match
         newNodes.forEach((node) => filterHierarchicalNodes(node))
         return newNodes
      })
   )

   constructor(private cdr: ChangeDetectorRef) {}

   scroll() {
      let { found, index } = this.CalculateIndex(this._allProducts, (n) => n.key === this.selectedProduct?.key)
      if (found) {
         setTimeout(() => {
            this.tree.scrollToVirtualIndex(index)
            this.cdr.detectChanges()
         }, 200)
      }
   }

   private CalculateIndex(tree: TreeNode[], predicate: (node: TreeNode) => boolean, startIndex: number = 0): { found: boolean; index: number } {
      let index: number = startIndex
      let found = false
      for (const node of tree) {
         found = predicate(node)
         if (found) {
            break
         }

         index++
         if (node.expanded) {
            ;({ found, index } = this.CalculateIndex(node.children, predicate, index))
            if (found) {
               break
            }
         }
      }

      return { found, index }
   }

   showPanel($event) {
      this.overlayPanel.toggle($event)
      //console.log(this.initialValue);
      this.selectedProduct = ProductFunctions.findAndExpandCollapse({ children: this._allProducts }, this.selectedProduct?.key, true)
      this.scroll()
   }

   onProductSelected($event: TreeNodeSelectEvent) {
      this.onChanged.emit({
         pimsProduct: this.pimsProduct,
         newValue: +this.selectedProduct?.key,
      })
      this.initialValue = +this.selectedProduct?.key
      this.overlayPanel.hide()
   }
}
